import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import PartsCatalogDetail from "@/pages/PartsCatalogDetail";
import { resolvePartsSlug, loadPartsSeo } from "@/lib/seo-status";

const SITE = "https://www.rexandsonsrvs.com";

export const Route = createFileRoute("/parts-catalog/$id")({
  // Discontinued parts used to render an empty "Product not found" body with a
  // 200 status (Search Console: ~700 Soft 404s). Answer with a real 404, and
  // 301 the legacy prefix-less slugs to their canonical URL server-side.
  loader: async ({ params }) => {
    const result = await resolvePartsSlug(params.id);
    if (result.kind === "redirect") {
      throw redirect({ href: result.to, statusCode: 301 });
    }
    if (result.kind === "missing") {
      throw notFound();
    }
    // Price + identifiers in the server HTML so Google Merchant's crawler sees them.
    return { seo: await loadPartsSeo(params.id) };
  },
  head: ({ loaderData, params }) => {
    const s = loaderData?.seo;
    if (!s) return {};
    const url = `${SITE}/parts-catalog/${s.slug || params.id}`;
    const title = `${s.title} | Rex & Sons RVs`;
    const jsonLd: Record<string, any> = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: s.title,
      description: s.description,
      sku: s.sku,
      url,
    };
    if (s.images.length) jsonLd["image"] = s.images;
    if (s.brand) jsonLd["brand"] = { "@type": "Brand", name: s.brand };
    if (s.mpn) jsonLd["mpn"] = s.mpn;
    if (s.gtin) jsonLd[s.gtin.length === 12 ? "gtin12" : s.gtin.length === 13 ? "gtin13" : s.gtin.length === 14 ? "gtin14" : "gtin8"] = s.gtin;
    if (s.category) jsonLd["category"] = s.category;
    if (s.price != null) {
      jsonLd["offers"] = {
        "@type": "Offer",
        url,
        priceCurrency: "USD",
        price: s.price.toFixed(2),
        itemCondition: "https://schema.org/NewCondition",
        availability: s.inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        seller: { "@type": "Organization", name: "Rex & Sons RVs" },
      };
    }
    const meta: any[] = [
      { title },
      { name: "description", content: s.description },
      { property: "og:title", content: title },
      { property: "og:description", content: s.description },
      { property: "og:type", content: "product" },
      { property: "og:url", content: url },
      { name: "twitter:card", content: "summary_large_image" },
    ];
    if (s.price != null) {
      meta.push(
        { property: "product:price:amount", content: s.price.toFixed(2) },
        { property: "product:price:currency", content: "USD" },
      );
    }
    if (s.image) {
      meta.push({ property: "og:image", content: s.image }, { name: "twitter:image", content: s.image });
    }
    return {
      meta,
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(jsonLd) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
              { "@type": "ListItem", position: 2, name: "Parts Catalog", item: `${SITE}/parts-catalog` },
              { "@type": "ListItem", position: 3, name: s.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: PartsCatalogDetail,
});
